import type { Router, RouteLocationNormalizedLoaded } from 'vue-router';
import { getAnalytics, setAnalyticsCurrentPage, syncAnalyticsConsent } from '@/services/analytics';
import { isPrivateTrackingUrl } from '@/utils/tracking.utils';

export default function setupAnalytics(router: Router): void {
  setAnalyticsCurrentPage(() => {
    const route = router.currentRoute.value;
    return route.matched.length && !isPrivateTrackingUrl(route.fullPath) ? buildTrackedPagePath(route) : undefined;
  });

  router.beforeEach((to, from) => {
    if (from.matched.length && !isPrivateTrackingUrl(from.fullPath) && isPrivateTrackingUrl(to.fullPath)) {
      // Already loaded SDK listeners cannot be unloaded. Auth links need a fresh, tracker-free document.
      window.location.assign(to.fullPath);
      return false;
    }
  });

  router.afterEach((to, from, failure) => {
    if (failure || to.fullPath === from.fullPath || isPrivateTrackingUrl(to.fullPath)) return;
    // Different records can normalize to the same URL, but an actual class/student change is still a pageview.
    const queryCleanup = to.path === from.path && buildTrackedPagePath(to) === buildTrackedPagePath(from);
    if (!queryCleanup) getAnalytics().trackPageview(buildTrackedPagePath(to));
    syncAnalyticsConsent();
  });
}

function buildTrackedPagePath(to: RouteLocationNormalizedLoaded): string {
  const pattern = to.matched.at(-1)?.path ?? to.path;
  const path = pattern.replace(/\/+$/, '') || '/';
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(to.query)) {
    if (!key.startsWith('utm_')) continue;
    for (const entry of Array.isArray(value) ? value : [value]) {
      if (entry != null) query.append(key, entry);
    }
  }
  return query.size ? `${path}?${query}` : path;
}
